import {ActionIcon, createStyles, Group, Paper, Stack, Table, Text, Anchor} from '@mantine/core';
import React, {useState} from 'react';

import {WarehouseLogin} from 'constants/WarehouseConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import notifications, {Response} from 'notifications';
import {UserTableUser} from 'tenants/admin/__generated/UserTableUser';
import {ConfirmRemoveAPIKeyModal} from 'tenants/admin/ManageAPIKeyModal';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import * as DateUtil from 'util/DateUtil';
import * as WarehouseDAO from 'warehouse/WarehouseDAO';

import {UserPageQuery} from './__generated/UserPageQuery';
import ResetMfaModal from './ResetMfaModal';
import type {RefetchUserPage} from './UserPage';

type UserPageQueryUser = ExtractByTypename<UserPageQuery['user'], 'UserNode'>;

const useStyles = createStyles((theme) => ({
  table: {
    '& thead th': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  mfaEnabledIcon: {
    color: theme.colors.green[8],
  },
}));

export function WarehouseCredentialsTable({
  user,
  refetch,
}: {
  user: UserPageQueryUser;
  refetch: RefetchUserPage;
}): JSX.Element {
  const {classes} = useStyles();
  const deleteWarehouseCredentials = async (loginId: string, username: string): Promise<void> => {
    const confirmed = await runConfirmDeleteModal({
      typeName: 'Credentials',
      title: 'Confirm Revoking Credential',
      message: `Are you sure you want to revoke these credentials for ${username}? They cannot be recovered.`,
      acceptText: 'Revoke',
    });
    if (!confirmed) {
      return;
    }
    try {
      // TODO(BNCH-167814) Fix Warehouse Delete for User Profile TAC
      await WarehouseDAO.revokeLogin({id: loginId} as WarehouseLogin);
      notifications.success('Credentials have been revoked.');
      await refetch();
    } catch (resp) {
      notifications.notifyError(resp as Response);
      throw resp;
    }
  };

  return (
    <Stack spacing="sm" w="100%">
      <Text c="dimmed">Manage credentials to the warehouse.</Text>
      <Table
        withBorder={true}
        c="dimmed"
        verticalSpacing="sm"
        horizontalSpacing="md"
        className={classes.table}
      >
        <thead>
          <tr>
            <th>Credentials</th>
            <th>Created</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {user.activeWarehouseLogins.map((login) => (
            <tr key={login.id}>
              <td>
                <Group spacing="xs">
                  <BenchlingIcon icon="warehouse-table" />
                  <Group spacing="xs" align="flex-start">
                    <Text weight="bold">{login.username}</Text>
                    <Text>{login.label}</Text>
                  </Group>
                </Group>
              </td>
              <td>{DateUtil.readableTime(login.createdAt)}</td>
              <td>
                <Group position="right">
                  <ActionIcon
                    aria-label={`Delete warehouse credential ${login.username}`}
                    onClick={async () => {
                      await deleteWarehouseCredentials(login.id, login.username);
                    }}
                  >
                    <BenchlingIcon icon="delete" />
                  </ActionIcon>
                </Group>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Stack>
  );
}

export function UserAPIKeySection({
  user,
  refetch,
}: {
  user: UserPageQueryUser;
  refetch: RefetchUserPage;
}): JSX.Element {
  return (
    <Stack spacing="sm" w="100%">
      <Text c="dimmed">Manage API keys for users with Developer Platform access.</Text>
      <Paper withBorder={true} radius="sm" p="sm">
        <Group position="apart">
          <Group spacing="md">
            <Paper py="xs" px="sm" bg="gray.1" radius="sm">
              <BenchlingIcon icon="key" />
            </Paper>
            <Stack spacing="xs">
              <Text>{user.name}'s API Key</Text>
              <Text c="dimmed">
                {user.apiKey?.createdAt && `Created ${DateUtil.readableTime(user.apiKey.createdAt)}`}
              </Text>
            </Stack>
          </Group>
          <ActionIcon
            aria-label={`Delete API key for ${user.name}`}
            onClick={() => {
              legacyRunModal(ConfirmRemoveAPIKeyModal, {
                user: {id: user.id, name: user.name} as UserTableUser,
                onRefreshUserListing: refetch,
              });
            }}
          >
            <BenchlingIcon icon="delete" />
          </ActionIcon>
        </Group>
      </Paper>
    </Stack>
  );
}

export function UserMFASection({
  user,
  refetch,
}: {
  user: UserPageQueryUser;
  refetch: RefetchUserPage;
}): JSX.Element {
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const {classes} = useStyles();
  return (
    <>
      {user.mfaEnabled ? (
        <Group spacing="sm">
          <BenchlingIcon icon="check-circle" className={classes.mfaEnabledIcon} />
          <Text>Enabled</Text>
          {user.permissions.canResetMfa && (
            <Anchor
              component="button"
              type="button"
              onClick={() => {
                setResetModalOpen(true);
              }}
            >
              Reset
            </Anchor>
          )}
        </Group>
      ) : (
        <Text>Not Configured</Text>
      )}
      {resetModalOpen && (
        <ResetMfaModal
          userName={user.name}
          userEmail={user.email}
          userApiId={user.id}
          onClose={() => {
            setResetModalOpen(false);
          }}
          onReset={async () => {
            setResetModalOpen(false);
            await refetch();
          }}
        />
      )}
    </>
  );
}
